/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-comment-textnodes */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CancelIcon from '@material-ui/icons/Cancel';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import ScheduleIcon from '@material-ui/icons/Schedule';
import TrafficIcon from '@material-ui/icons/Traffic';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import EmojiTransportation from '@material-ui/icons/EmojiTransportation';
import CommuteIcon from '@material-ui/icons/Commute';
import IconButton from '@material-ui/core/IconButton';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

import LoadingPageText from '../../../common/Loading/LoadingPageText';
import PatientDetails from './PatientDetails';
import ActivityDetails from './ActivityDetails';

import useTimeline from '../../timeline/useTimeline';
import { useFetchVisitDetails, postUnassignClinicianFromTreatmentInstance } from '../../../../actions/areas/planner';
import { startEndTimeDisplay, parseDateUtc } from '../../../../utils/dates';
import { checkFeatureAccess } from '../../../../utils/helpers';
import globals from '../../../../globals/communityGlobals';
import TreatmentDetails from '../UnassignedTreatments/TreatmentDetails';
import { AssignmentVisitState, VisitType, AuditTrailType } from '../../../../enums';
import { cardTypes } from '../../../../settings/constants/cardTypes';
import AuditTrail from '../../../common/AuditTrail/AuditTrail';
import BundleVisitActivities from '../../BundleVisit/BundleVisitActivities';
import ChangeTreatmentNotes from '../ChangeTreatmentNotes/ChangeTreatmentNotes';
import { TreatmentInstanceOccurrenceType } from '../../../../enums/treatmentInstanceOccurrenceType';

const locale = globals.getArea('treatments');

// eslint-disable-next-line react/prop-types
export default function AssignmentDetails({ refreshGrid, period, isMileageCapEnabled, includeTreatments }) {
  const faList = useSelector(store => store.global.auth.featureAccessList);

  const [state, setState] = useState({ forceRefresh: false, tab: 1 });
  const [errorMsg, setErrorMsg] = useState('');

  const {
    activeAssignment: assignment,
    setActiveAssignment,
    setActiveTreatmentInstanceForCopy,
    setFlag,
    getFlag,
    startLiveTrackingInterval,
    stopLiveTrackingInterval,
    setAlertMessage,
  } = useTimeline();

  if (!assignment) return null;

  const { data: treatmentDetails, isDataLoading, auth, error } = useFetchVisitDetails(
    assignment.treatmentInstanceId,
    state.forceRefresh,
  );

  useEffect(() => {
    stopLiveTrackingInterval();
    return () => startLiveTrackingInterval();
  }, []);

  const isBundled = assignment.isBundled;
  useEffect(() => {
    if (error) setAlertMessage({ message: error });
  }, [isDataLoading]);

  const canAllocateWork = checkFeatureAccess(faList, globals.applicationFeatureAccesses.canAllocateWork);
  const isBehindNow = parseDateUtc(assignment.visitTime.actual.startDate).isBefore(parseDateUtc(globals.now));
  const isCompleted = assignment.visitStatus === AssignmentVisitState.Complete;
  const isConvertedVisit = () =>
    treatmentDetails &&
    treatmentDetails.treatmentInstance.occurrenceType === TreatmentInstanceOccurrenceType.ConvertedFromOnSiteVisit;

  const canUnassignVisit = () => {
    if (
      !(
        assignment.visitStatus === AssignmentVisitState.Planned ||
        assignment.visitStatus === AssignmentVisitState.Behind
      )
    )
      return false;

    if (parseDateUtc(assignment.visitTime.actual.startDate).isSameOrAfter(parseDateUtc(globals.now))) return true;
    const isToday = parseDateUtc(globals.now).isBetween(parseDateUtc(period.startDate), parseDateUtc(period.endDate));
    if (isToday) {
      return (
        parseDateUtc(assignment.visitTime.actual.startDate).isSameOrAfter(parseDateUtc(period.startDate)) &&
        parseDateUtc(assignment.visitTime.actual.startDate).isBefore(parseDateUtc(period.endDate))
      );
    }
    return false;
  };

  const copyTreatment = () => {
    setActiveTreatmentInstanceForCopy(treatmentDetails.treatmentInstance);
  };

  // const visitCanBeConverted = () => {
  //   return (
  //     assignment.visitType === VisitType.OnSite &&
  //     assignment.cardType === cardTypes.MASTER &&
  //     parseDateUtc(assignment.visitTime.actual.startDate).isSameOrAfter(parseDateUtc(globals.now)) &&
  //     !isBundled &&
  //     treatmentDetails.treatment.additionalClinician === 0 &&
  //     assignment.visitStatus === AssignmentVisitState.Planned
  //   );
  // };

  const containerClass = classNames('sidePanel-header', {
    'card-status-red': assignment.cardStatus.name.toLowerCase() === 'red',
    'card-status-amber': assignment.cardStatus.name.toLowerCase() === 'amber',
    'card-status-green': assignment.cardStatus.name.toLowerCase() === 'green',
    'card-status-completed': isCompleted,
    'card-status-ahead':
      assignment.visitStatus === AssignmentVisitState.Ahead ||
      assignment.visitStatus === AssignmentVisitState.StartedLate ||
      assignment.visitStatus === AssignmentVisitState.OnTime,
    // 'card-status-ontime': assignment.visitStatus === AssignmentVisitState.OnTime, -- removed green color due to introducing started late, every started visit is blue
    'card-status-behind': assignment.visitStatus === AssignmentVisitState.Behind,
  });

  const postcodeClass = classNames({
    'is-treatment-address': treatmentDetails !== null && treatmentDetails.treatment.address,
  });

  const isAheadOrOnTime = () => {
    return (
      assignment.visitStatus === AssignmentVisitState.Ahead || assignment.visitStatus === AssignmentVisitState.OnTime
    );
  };

  const headerRender = () => {
    const visitStatus = assignment.visitStatus === AssignmentVisitState.StartedLate ? 'Late' : assignment.visitStatus;
    const activeDeactiveBtnRender = () => {
      if (assignment.visitStatus === AssignmentVisitState.Complete) return null;

      return (
        <List component="nav">
          {!isAheadOrOnTime() && canAllocateWork && canUnassignVisit() && (
            <ListItem
              className="header-action-button"
              key={'unassign-btn'}
              button
              onClick={() => {
                setActiveAssignment(null);
                postUnassignClinicianFromTreatmentInstance(auth, { id: assignment.id }, refreshGrid, setAlertMessage);
              }}
            >
              <ListItemIcon>
                <RemoveCircleIcon />
              </ListItemIcon>
              <ListItemText primary={'Unassign visit'} />
            </ListItem>
          )}
          {!isAheadOrOnTime() && canAllocateWork && !isBehindNow && assignment.visitType !== VisitType.PhoneCall && (
            <ListItem className="header-action-button" key={'copy-btn'} button onClick={copyTreatment}>
              <ListItemIcon>
                <span className="icon-extend icon-copy" />
              </ListItemIcon>
              <ListItemText primary={'Copy visit'} />
            </ListItem>
          )}
          {canAllocateWork && treatmentDetails && treatmentDetails.treatmentInstance.masterId === null && (
            <ListItem
              className="header-action-button"
              key={'add-notes-btn'}
              button
              onClick={() => setFlag('showTreatmentNotesSidePanel', true)}
            >
              <ListItemIcon>
                <NoteAddIcon />
              </ListItemIcon>
              <ListItemText primary={'Add Notes'} />
            </ListItem>
          )}
          {/* {canAllocateWork && treatmentDetails && visitCanBeConverted() && (
            <ListItem
              className="header-action-button"
              key={'convert-visit-btn'}
              button
              onClick={() => setFlag('showConvertVisitSidePanel', true)}
            >
              <ListItemIcon />
              <ListItemText primary={'Convert Visit'} />
            </ListItem>
          )} */}
        </List>
      );
    };

    const headerTitle = isCompleted
      ? locale.getMsg(isBundled ? 'completedBundleVisit' : 'completedVisit')
      : locale.getMsg(isBundled ? 'assignedBundleVisit' : 'assignedVisit');

    return (
      <div className={containerClass}>
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1}>
              <Typography variant="subtitle1">
                {headerTitle} #{assignment.treatmentInstanceId}
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={() => setActiveAssignment(null)}>
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
        <div className="sidePanel-header-wrapper">
          <div className="sidePanel-header-left-info-wrap">
            <div className="sidePanel-header-title">
              <Typography variant="h2">
                {assignment.secondaryLabel}
                <span
                  className={`icon-rag-${assignment.ragStatus.name.toLowerCase()} icon-svg asw-icon rag-status-icon`}
                />
              </Typography>
              <Typography variant="h3" gutterBottom className={postcodeClass}>
                {assignment.visitType === VisitType.PhoneCall ? assignment.contactNumber : assignment.postcode}
              </Typography>
            </div>
            <div className="sidePanel-header-info patient-header">
              <Box className="assignment-desc-info" display="flex" flexDirection="row">
                <Box display="flex" flexDirection="column">
                  <Box className="header-icon-wrapper" display="flex" flexDirection="row">
                    <ScheduleIcon />
                    <span className="info-text">
                      {isCompleted
                        ? startEndTimeDisplay(
                            assignment.actualOccurance.actual.startDate,
                            assignment.actualOccurance.actual.endDate,
                          )
                        : startEndTimeDisplay(
                            assignment.visitTime.actual.startDate,
                            assignment.visitTime.actual.endDate,
                          )}
                    </span>
                  </Box>
                  <Box className="header-icon-wrapper">
                    <TrafficIcon />
                    <span className="info-text">{visitStatus || ''}</span>
                  </Box>
                  <Box display="flex" alignItems="center">
                    {treatmentDetails &&
                      treatmentDetails.treatment.preferredClinicianId &&
                      treatmentDetails.treatmentInstance.cardType === cardTypes.MASTER && (
                        <Box className="header-info-icon">
                          <div className="person-star">
                            <span className="icon-extend icon-person-star" />
                          </div>
                        </Box>
                      )}
                    {!isCompleted && assignment.runsOvernight && (
                      <Box className="header-info-icon">
                        <div className="night-shift-icon">
                          <span className="icon-extend icon-night" />
                        </div>
                      </Box>
                    )}
                    {isBundled && (
                      <Box className="header-info-icon">
                        <div className="bundle-visits-icon">
                          <span className="icon-extend icon-bundle-visits" />
                        </div>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column">
                  {assignment.visitStatus === AssignmentVisitState.Planned &&
                    assignment.visitType !== VisitType.PhoneCall && (
                      <Box className="header-icon-wrapper">
                        {assignment.travelTime && assignment.travelTime.actual.duration > 0 ? (
                          <React.Fragment>
                            <DirectionsCarIcon />
                            <span className="info-text">
                              {startEndTimeDisplay(
                                assignment.travelTime.actual.startDate,
                                assignment.travelTime.actual.endDate,
                              )}
                            </span>
                          </React.Fragment>
                        ) : (
                          <span className="icon-extend icon-svg asw-icon icon-direction-car-no" />
                        )}
                      </Box>
                    )}
                  {isMileageCapEnabled && assignment.plannedTravelDistance !== null && (
                    <Box className="header-icon-wrapper">
                      <CommuteIcon />
                      <span className="info-text">{assignment.plannedTravelDistance}</span>
                    </Box>
                  )}
                </Box>
                <Box display="flex" flexDirection="column">
                  {isMileageCapEnabled && assignment.plannedTravelDistanceToBase !== null && (
                    <Box className="header-icon-wrapper">
                      <EmojiTransportation />
                      <span className="info-text">{assignment.plannedTravelDistanceToBase}</span>
                    </Box>
                  )}
                  <Box className="header-icon-wrapper" display="flex" flexDirection="row">
                    {assignment.isReviewDate && (
                      <Box className="header-icon-wrapper">
                        <span className="icon-review icon-svg asw-icon" />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box className="header-icon-wrapper" display="flex" flexDirection="row">
                  <span className="icon-extend icon-medic icon-small" />
                  <span className="info-text">
                    {assignment.treatmentAdditionalClinician > 0
                      ? `${assignment.numberOfClinicians - assignment.treatmentAdditionalClinician} + ${
                          assignment.treatmentAdditionalClinician
                        }`
                      : assignment.numberOfClinicians}
                  </span>
                </Box>
                {treatmentDetails && isConvertedVisit() && (
                  <Box className="header-icon-wrapper">
                    <span className="icon-convert-visit-call icon-svg asw-icon" />
                    <span className="info-text">{locale.getMsg('converted')}</span>
                  </Box>
                )}
              </Box>
            </div>
          </div>

          <div className="sidePanel-header-action">{activeDeactiveBtnRender()}</div>
        </div>
      </div>
    );
  };

  const assignmentDetailsRender = () => {
    return (
      <React.Fragment>
        <div className="sidePanel-content">
          <Grid container spacing={0}>
            <Grid item xs={12} sm={12}>
              <Tabs
                className="custom-tabs"
                value={state.tab}
                onChange={(event, value) => setState({ tab: value })}
                centered
              >
                <Tab label={isBundled ? locale.getMsg('visits') : locale.getMsg('activities')} />
                <Tab label="Patient" />
                <Tab label="Treatment" style={{ display: !isBundled ? 'block' : 'none' }} />
                {assignment.treatmentInstanceId > 0 && <Tab label="Audit Trails" />}
              </Tabs>
            </Grid>
            <Grid item xs={12}>
              {state.tab === 0 &&
                (!isBundled ? (
                  <Box>
                    <ActivityDetails treatmentDetails={treatmentDetails} />
                  </Box>
                ) : (
                  <Box>
                    <span className="form-validation-error">
                      <p>{errorMsg}</p>
                    </span>
                    <BundleVisitActivities
                      items={treatmentDetails.treatmentInstance.originalTreatmentInstances}
                      treatmentInstance={treatmentDetails.treatmentInstance}
                      overiddenDuration={treatmentDetails.treatmentInstance.overriddenDuration}
                      errorMsg={setErrorMsg}
                      isBundled
                    />
                  </Box>
                ))}
              {state.tab === 1 && treatmentDetails !== null && (
                <Box>
                  <PatientDetails patientDetails={treatmentDetails.patient} />
                </Box>
              )}
              {state.tab === 2 && treatmentDetails !== null && !isBundled && (
                <Box>
                  <TreatmentDetails
                    treatmentInstance={treatmentDetails.treatmentInstance}
                    treatment={treatmentDetails.treatment}
                    lastVisitedInfo={{
                      clinicianName: treatmentDetails.lastVisitedClinicianFullName,
                      lastVisitedTime: treatmentDetails.lastVisitedTime,
                    }}
                    canEdit={false}
                  />
                </Box>
              )}
              {state.tab === 3 && (
                <Box>
                  <AuditTrail auditTrailType={AuditTrailType.Visit} entityId={assignment.treatmentInstanceId} />
                </Box>
              )}
            </Grid>
          </Grid>
        </div>
      </React.Fragment>
    );
  };

  return (
    <section className="side-panel-content-wrapper" id="assignment-side-panel">
      <SwipeableDrawer
        anchor="right"
        open
        onOpen={() => {}}
        onClose={() => {}}
        ModalProps={{
          onBackdropClick: () => setActiveAssignment(null),
        }}
      >
        <div className="sidePanel-wrapper" style={{ width: '635px' }} id="assigned-side-panel">
          {headerRender()}
          {!isDataLoading ? (
            assignmentDetailsRender()
          ) : (
            <LoadingPageText showHeader={false}>Wait a moment while we gather treatment info...</LoadingPageText>
          )}
        </div>
        {getFlag('showTreatmentNotesSidePanel') === true && treatmentDetails && (
          <ChangeTreatmentNotes
            treatmentDetails={treatmentDetails}
            toggleRefresh={refreshGrid}
            includeTreatments={includeTreatments}
          />
        )}
        {/* {getFlag('showConvertVisitSidePanel') === true && treatmentDetails && (
          <ConvertTreatmentInstanceType treatmentDetails={treatmentDetails} />
        )} */}
      </SwipeableDrawer>
    </section>
  );
}
